import { memo } from "react";
import { Gavel, TrendingUp, Clock, Flame } from "lucide-react";
import MoneyIcon from "@/components/MoneyIcon";
import type { AuctionItem } from "@/types/auction";

interface StatsCardsProps {
  auctions: AuctionItem[];
  filteredCount: number;
  totalValue: number;
}

const formatValue = (value: number): string => {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return value.toLocaleString('de-DE');
};

const StatsCards = memo(({ auctions, filteredCount, totalValue }: StatsCardsProps) => {
  // Calculate ending soon (within 1 hour)
  const endingSoon = auctions.filter(a => {
    const timeLeft = new Date(a.endTime).getTime() - Date.now();
    return timeLeft > 0 && timeLeft < 3600000; // 1 hour
  }).length;

  // Calculate hot auctions (more than 5 unique bidders)
  const hotAuctions = auctions.filter(a => 
    a.bids && typeof a.bids === 'object' && Object.keys(a.bids).length > 5
  ).length;

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {/* Active Auctions */}
      <div className="rounded-xl border border-border/50 bg-card p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
          <Gavel className="w-5 h-5 text-primary" />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground truncate">Aktive Auktionen</p>
          <p className="text-xl font-bold text-foreground">{filteredCount}</p>
        </div>
      </div>

      {/* Total Value */}
      <div className="rounded-xl border border-border/50 bg-card p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-chart-4/10 flex items-center justify-center shrink-0">
          <MoneyIcon size={20} className="text-chart-4" />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground truncate">Gesamtwert</p>
          <p className="text-xl font-bold text-foreground">{formatValue(totalValue)}</p>
        </div>
      </div>

      {/* Ending Soon */}
      <div className="rounded-xl border border-border/50 bg-card p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-chart-5/10 flex items-center justify-center shrink-0">
          <Clock className="w-5 h-5 text-chart-5" />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground truncate">Endet bald</p>
          <p className="text-xl font-bold text-foreground">{endingSoon}</p>
        </div>
      </div>

      {/* Hot Auctions */}
      <div className="rounded-xl border border-border/50 bg-card p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-chart-3/10 flex items-center justify-center shrink-0">
          <Flame className="w-5 h-5 text-chart-3" />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground truncate">Heiß begehrt</p>
          <p className="text-xl font-bold text-foreground">{hotAuctions}</p>
        </div>
      </div>
    </div>
  );
});

StatsCards.displayName = 'StatsCards';

export default StatsCards;
